import { Component, inject, signal, OnInit, ViewChild, DestroyRef, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ReactiveFormsModule, FormBuilder, Validators, FormsModule } from '@angular/forms';

import { SourceArticle } from "../article-view/source-article/source-article";
import { Municipality } from '../../service/municipality.service';
import { GeneralService } from '../../service/general.service';
import { SourceService } from '../../service/source.service';

import { DepartmentsGT } from '../../enum/departments.enum';
import { MunicipalityGT } from '../../enum/municipalities.enum';
import { SourceContact, SourceContactsService } from '../../service/source-contacts.service';
import { CodigoPostalService } from '../../service/codigo-postal.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, debounceTime, distinctUntilChanged, finalize, forkJoin, map, of, switchMap } from 'rxjs';
import { SourceSurveyService } from '../../service/source-survey';
import { HostListener } from '@angular/core';
import { CanComponentDeactivate } from '../../guards/unsaved-changes.guard';
import { Router } from '@angular/router';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { SourceSurveyPayload, SourceSurveyResponse } from '../../models/source-survey.models';
import { SuccessAlertModalComponent } from '../../shared/components/success-alert-modal/success-alert-modal';
import { ValidationSummaryModalComponent } from '../../shared/components/validation-summary-modal/validation-summary-modal';

interface UiContact extends SourceContact {
  _editing?: boolean;
  _isNew?: boolean;
}

interface CodigoPostalQuery {
  depId: number;
  municipioId: number;
  referencia: string;
}

interface CodigoPostalLookupResult {
  lista: any[];
  preserveCurrentState: boolean;
}

interface MobileSelectOption {
  value: string | number;
  label: string;
}

type MobileSelectControlName =
  | 'fkDepartamento'
  | 'fkMunicipio'
  | 'fkUsuario'
  | 'periodo'
  | 'fkPeriodicidadVisita'
  | 'fkDiaVisita'
  | 'requiereAudio'
  | 'requiereImagen'
  | 'fkActividadEmpresa'
  | 'fkTamanoEmpresa'
  | 'estado';

const DEFAULT_EMAIL = 'nd@nd.com';
const DEFAULT_JORNADAS = {
  horaInicioPrimeraJornada: '08:00',
  horaFinalPrimeraJornada: '12:00',
  horaInicioSegundaJornada: '13:00',
  horaFinalSegundaJornada: '16:00'
};
const PHONE_TEXT_PATTERN = /^[A-Za-z0-9áéíóúÁÉÍÓÚñÑ\s()+\-./#]*$/;
const NIT_TEXT_PATTERN = /^[A-Za-z0-9\s/-]*$/;

const jornadasCongruentesValidator: ValidatorFn = (
  group: AbstractControl
): ValidationErrors | null => {

  const h1i = group.get('horaInicioPrimeraJornada')?.value;
  const h1f = group.get('horaFinalPrimeraJornada')?.value;
  const h2i = group.get('horaInicioSegundaJornada')?.value;
  const h2f = group.get('horaFinalSegundaJornada')?.value;

  const toMinutes = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  let errors: ValidationErrors = {};

  // =========================
  // PRIMERA JORNADA
  // =========================
  if (h1i && h1f) {
    const i1 = toMinutes(h1i);
    const f1 = toMinutes(h1f);

    if (f1 <= i1) {
      errors['primeraJornadaInvalida'] = true;
    }

    if (f1 - i1 > 12 * 60) {
      errors['primeraJornadaExcesiva'] = true;
    }
  }

  // =========================
  // SEGUNDA JORNADA
  // =========================
  if (h2i && h2f) {
    const i2 = toMinutes(h2i);
    const f2 = toMinutes(h2f);

    if (f2 <= i2) {
      errors['segundaJornadaInvalida'] = true;
    }

    if (f2 - i2 > 12 * 60) {
      errors['segundaJornadaExcesiva'] = true;
    }

    if (h1i && h1f) {
      const f1 = toMinutes(h1f);

      if (i2 < f1) {
        errors['traslapeJornadas'] = true;
      }
    }
  }

  return Object.keys(errors).length ? errors : null;
};



@Component({
  selector: 'app-source-fieldwork',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, SourceArticle, SuccessAlertModalComponent, ValidationSummaryModalComponent],
  templateUrl: './source-fieldwork.html',
  styleUrl: './source-fieldwork.scss'
})
export class SourceFieldworkComponent implements OnInit, CanComponentDeactivate {
  private readonly destroyRef = inject(DestroyRef);
  private readonly codigoPostalQuery$ = new Subject<CodigoPostalQuery>();


  private surveyService = inject(SourceSurveyService);

  @ViewChild(SourceArticle)
  articleCmp?: SourceArticle;

  private fb = inject(FormBuilder);
  private sourceService = inject(SourceService);
  private municipalityService = inject(Municipality);
  private generalService = inject(GeneralService);
  //private sourceContactService = inject(SourceContactsService);
  private codigoPostalService = inject(CodigoPostalService);
  hasUnsavedChanges = signal(false);
  private router = inject(Router);

  constructor() {
    this.setupCodigoPostalLookup();
  }



  /** =====================
   *  CONSTANTES
   *  ===================== */


  /** =====================
   *  STATE
   *  ===================== */
  createdSourceId = 0;


  pendingContacts: UiContact[] = [];
  savedContacts: UiContact[] = [];


  usuarios = signal<any[]>([]);
  periodicidades = signal<any[]>([]);
  actividades = signal<any[]>([]);
  tamanios = signal<any[]>([]);
  codigosPostales = signal<any[]>([]);
  codigoPostalDescripcion = '';
  diasVisita = signal<any[]>([]);

  departaments = Object.entries(DepartmentsGT)
    .filter(([_, v]) => !isNaN(Number(v)))
    .map(([k, v]) => ({ id: Number(v), nombre: k }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));

  periodoTitulo = signal<string>('');
  periodoLimite = signal<number>(0);

  municipalities = signal<{ id: number; nombre: string }[]>([]);

  loading = signal(false);
  geoLoading = signal(false);
  generatingNumber = signal(false);
  success = signal(false);
  showValidationModal = signal(false);
  successMessage = signal('La fuente se subió exitosamente.');
  error = signal<string | null>(null);
  savingContacts = signal(false);
  showContactForm = signal(false);
  editingIndex: number | null = null;
  readonly mobileSelectOpen = signal(false);
  readonly mobileSelectTitle = signal('');
  readonly mobileSelectControl = signal<MobileSelectControlName | null>(null);
  readonly mobileSelectOptions = signal<MobileSelectOption[]>([]);
  readonly mobileSelectQuery = signal('');
  readonly filteredMobileSelectOptions = computed(() => {
    const query = this.normalizeSearchText(this.mobileSelectQuery());
    const options = this.mobileSelectOptions();

    if (!query) {
      return options;
    }

    return options.filter((option) =>
      this.normalizeSearchText(option.label).includes(query)
    );
  });
  readonly yesNoOptions: MobileSelectOption[] = [
    { value: 'S', label: 'Sí' },
    { value: 'N', label: 'No' }
  ];
  readonly statusOptions: MobileSelectOption[] = [
    { value: 'A', label: 'Activo' },
    { value: 'P', label: 'Pendiente' },
    { value: 'I', label: 'Inactivo' }
  ];


  missingFields = signal<string[]>([]);

  /** =====================
   *  FORM
   *  ===================== */
  form = this.fb.group({
    codigoINE: ['', Validators.required],
    razonSocial: ['', Validators.required],
    nombre: ['', Validators.required],
    direccion: ['', Validators.required],

    fkCodigoPostal: this.fb.control<number | null>(null, Validators.required),
    fkDepartamento: [null, Validators.required],
    fkMunicipio: [null, Validators.required],
    fkUsuario: [null, Validators.required],
    fkPeriodicidadVisita: [null, Validators.required],
    fkDiaVisita: [null, Validators.required],
    periodo: [null, Validators.required],
    ordenRuta: this.fb.control<number | null>(0, [Validators.min(0)]),

    zona: [null],

    gpsLatitud: this.fb.control<number | null>(null, Validators.required),
    gpsLongitud: this.fb.control<number | null>(null, Validators.required),
    gpsAltitud: this.fb.control<number | null>(null),
    gpsFecha: this.fb.control<string | null>(null, Validators.required),

    telefonos: ['', [Validators.pattern(PHONE_TEXT_PATTERN)]],
    email: ['', [Validators.email]],

    horaInicioPrimeraJornada: [DEFAULT_JORNADAS.horaInicioPrimeraJornada],
    horaFinalPrimeraJornada: [DEFAULT_JORNADAS.horaFinalPrimeraJornada],
    horaInicioSegundaJornada: [DEFAULT_JORNADAS.horaInicioSegundaJornada],
    horaFinalSegundaJornada: [DEFAULT_JORNADAS.horaFinalSegundaJornada],


    requiereAudio: ['N'],
    requiereImagen: ['N'],
    nit: ['', [Validators.required, Validators.pattern(NIT_TEXT_PATTERN)]],
    fkActividadEmpresa: [null, Validators.required],
    fkTamanoEmpresa: [null, Validators.required],
    codigoCIIU: [''],
    comentarios: [''],
    referencia: [''],
    informacionAdicional: [''],
    estado: ['P'],



  }, {
    validators: jornadasCongruentesValidator
  });

  /** Labels para mostrar qué falta */
  private readonly labels: Record<string, string> = {
    codigoINE: 'Código INE',
    razonSocial: 'Razón Social',
    nombre: 'Nombre',
    direccion: 'Dirección',
    fkCodigoPostal: 'Código Postal',
    fkDepartamento: 'Departamento',
    fkMunicipio: 'Municipio',
    fkUsuario: 'Usuario Responsable',
    fkPeriodicidadVisita: 'Periodicidad',
    fkDiaVisita: 'Día de Visita',
    periodo: 'Periodo',
    ordenRuta: 'Orden de Ruta',
    gpsLatitud: 'Latitud',
    gpsLongitud: 'Longitud',
    gpsFecha: 'Fecha GPS',
    nit: 'NIT',
    fkActividadEmpresa: 'Actividad Empresa',
    fkTamanoEmpresa: 'Tamaño Empresa'
  };

  /** =====================
   *  INIT
   *  ===================== */
  ngOnInit(): void {
    this.selectFirstDepartmentIfNeeded();
    this.loadCatalogs();

    if (!this.form.get('gpsFecha')?.value) {
      this.form.patchValue({ gpsFecha: this.nowLocalDateTime() }, { emitEvent: false });
    }
    this.form.valueChanges.subscribe(() => {
      this.hasUnsavedChanges.set(true);
    });


  }

  private setupCodigoPostalLookup(): void {
    this.codigoPostalQuery$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(
          (previous, current) =>
            previous.depId === current.depId &&
            previous.municipioId === current.municipioId &&
            previous.referencia === current.referencia
        ),
        switchMap(({ depId, municipioId, referencia }) =>
          this.codigoPostalService.getCodigosPostales(depId, municipioId, referencia).pipe(
            map((lista): CodigoPostalLookupResult => ({
              lista,
              preserveCurrentState: false
            })),
            catchError((error) => {
              if (this.isTransientCodigoPostalError(error)) {
                return of({
                  lista: this.codigosPostales(),
                  preserveCurrentState: true
                });
              }

              return of({
                lista: [],
                preserveCurrentState: false
              });
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ lista, preserveCurrentState }) => {
        if (preserveCurrentState) {
          return;
        }

        this.codigosPostales.set(lista);

        if (lista.length > 0) {
          this.form.patchValue({
            fkCodigoPostal: lista[0].pk
          });
          this.codigoPostalDescripcion = lista[0].descripcion;
        } else {
          this.form.patchValue({ fkCodigoPostal: null });
          this.codigoPostalDescripcion = '';
        }
      });
  }

  private isTransientCodigoPostalError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && (error.status === 0 || error.status === 502);
  }

  private normalizeSelectValue(value: unknown): string | null {
    if (value === null || value === undefined || value === '') return null;
    return String(value);
  }

  private setFirstFormOptionIfNeeded<T>(
    controlName: string,
    list: T[],
    getValue: (item: T) => unknown
  ): unknown {
    const control = this.form.get(controlName);
    if (!control) return null;

    const currentValue = this.normalizeSelectValue(control.value);
    const hasCurrentValue = currentValue != null && list.some(
      item => this.normalizeSelectValue(getValue(item)) === currentValue
    );

    if (hasCurrentValue) {
      return control.value;
    }

    const nextValue = list.length > 0 ? getValue(list[0]) : null;
    control.patchValue((nextValue ?? null) as any, { emitEvent: false });
    return nextValue;
  }

  private normalizeCatalogText(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  private prioritizeMensualPeriodicidad(list: any[]): any[] {
    const mensualIndex = list.findIndex(item =>
      this.normalizeCatalogText(item?.descripcion) === 'mensual'
    );

    if (mensualIndex <= 0) {
      return list;
    }

    const mensual = list[mensualIndex];
    return [mensual, ...list.filter((_, index) => index !== mensualIndex)];
  }

  private selectMensualPeriodicidadIfNeeded(list: any[]): void {
    const orderedList = this.prioritizeMensualPeriodicidad(list);
    this.periodicidades.set(orderedList);
    this.setFirstFormOptionIfNeeded('fkPeriodicidadVisita', orderedList, item => item?.pk);
  }

  private selectFirstDepartmentIfNeeded(): void {
    const selectedDepartment = this.setFirstFormOptionIfNeeded(
      'fkDepartamento',
      this.departaments,
      item => item.id
    );

    if (selectedDepartment != null) {
      this.onDepartamentoChange();
    }
  }

  private selectFirstPeriodoIfNeeded(): void {
    this.setFirstFormOptionIfNeeded(
      'periodo',
      this.periodos(),
      item => item
    );
  }

  loadCatalogs(): void {
    this.generalService.get('GET_USERS').subscribe(r => {
      const list = r ?? [];
      this.usuarios.set(list);
      this.setFirstFormOptionIfNeeded('fkUsuario', list, item => item?.pk);
    });

    this.generalService.get('GET_PERIODICIDAD_VISITA').subscribe(r => {
      this.selectMensualPeriodicidadIfNeeded(r ?? []);
    });

    this.generalService.get('GET_ACTIVIDADES_EMPRESA').subscribe(r => {
      const list = r ?? [];
      this.actividades.set(list);
      this.setFirstFormOptionIfNeeded('fkActividadEmpresa', list, item => item?.pk);
    });

    this.generalService.get('GET_TAMANO_EMPRESA').subscribe(r => {
      const list = r ?? [];
      this.tamanios.set(list);
      this.setFirstFormOptionIfNeeded('fkTamanoEmpresa', list, item => item?.pk);
    });

    this.generalService.get('GET_DIAS_VISITA').subscribe(r => {
      const list = r ?? [];
      this.diasVisita.set(list);
      this.setFirstFormOptionIfNeeded('fkDiaVisita', list, item => item?.pk);
    });
    this.generalService.get('GET_PARAMETROS_PERIODO')
      .subscribe((data: any[]) => {
        console.log('PARAMETROS PERIODO', data);

        (data ?? []).forEach(p => {
          if (p.codigo === 'PERIODO_TITULO') {
            this.periodoTitulo.set(p.valor);
          }

          if (p.codigo === 'PERIODO_NUMERO') {
            this.periodoLimite.set(Number(p.valor));
          }
        });

        this.selectFirstPeriodoIfNeeded();
      });
  }


  /*seg de parametros para esa parte */

  periodos(): number[] {
    const limite = this.periodoLimite();
    return limite > 0 ? Array.from({ length: limite }, (_, i) => i + 1) : [];
  }

  openMobileSelect(
    controlName: MobileSelectControlName,
    title: string,
    options: MobileSelectOption[]
  ): void {
    this.mobileSelectControl.set(controlName);
    this.mobileSelectTitle.set(title);
    this.mobileSelectOptions.set(options);
    this.mobileSelectQuery.set('');
    this.mobileSelectOpen.set(true);
  }

  closeMobileSelect(): void {
    this.mobileSelectOpen.set(false);
    this.mobileSelectControl.set(null);
    this.mobileSelectTitle.set('');
    this.mobileSelectOptions.set([]);
    this.mobileSelectQuery.set('');
  }

  updateMobileSelectQuery(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.mobileSelectQuery.set(input?.value ?? '');
  }

  applyMobileSelectOption(option: MobileSelectOption): void {
    const controlName = this.mobileSelectControl();
    if (!controlName) {
      return;
    }

    this.form.patchValue({ [controlName]: option.value } as any);

    if (controlName === 'fkDepartamento') {
      this.onDepartamentoChange();
    } else if (controlName === 'fkMunicipio') {
      this.onMunicipioChange();
    }

    this.closeMobileSelect();
  }

  isActiveMobileSelectOption(option: MobileSelectOption): boolean {
    const controlName = this.mobileSelectControl();
    if (!controlName) {
      return false;
    }

    return String(this.form.get(controlName)?.value ?? '') === String(option.value);
  }

  getMobileSelectionLabel(controlName: MobileSelectControlName, placeholder: string): string {
    const currentValue = this.form.get(controlName)?.value;
    const options = this.getMobileSelectionOptions(controlName);

    if (currentValue === null || currentValue === undefined || currentValue === '' || Number(currentValue) === 0) {
      return placeholder;
    }

    const option = options.find((item) => String(item.value) === String(currentValue));
    return option?.label ?? placeholder;
  }

  getDepartamentoMobileOptions(): MobileSelectOption[] {
    return this.departaments.map((department) => ({
      value: department.id,
      label: department.nombre
    }));
  }

  getMunicipioMobileOptions(): MobileSelectOption[] {
    return this.municipalities().map((municipality) => ({
      value: municipality.id,
      label: municipality.nombre
    }));
  }

  getUsuarioMobileOptions(): MobileSelectOption[] {
    return this.usuarios().map((user) => ({
      value: Number(user?.pk ?? 0),
      label: String(user?.nombre ?? user?.descripcion ?? '').trim()
    })).filter((option) => option.value > 0 && option.label);
  }

  getPeriodoMobileOptions(): MobileSelectOption[] {
    return this.periodos().map((period) => ({
      value: period,
      label: `${this.periodoTitulo()} ${period}`
    }));
  }

  getPeriodicidadMobileOptions(): MobileSelectOption[] {
    return this.periodicidades().map((item) => ({
      value: Number(item?.pk ?? 0),
      label: String(item?.descripcion ?? '').trim()
    })).filter((option) => option.value > 0 && option.label);
  }

  getDiaVisitaMobileOptions(): MobileSelectOption[] {
    return this.diasVisita().map((item) => ({
      value: Number(item?.pk ?? 0),
      label: String(item?.descripcion ?? '').trim()
    })).filter((option) => option.value > 0 && option.label);
  }

  getActividadMobileOptions(): MobileSelectOption[] {
    return this.actividades().map((item) => ({
      value: Number(item?.pk ?? 0),
      label: String(item?.descripcion ?? '').trim()
    })).filter((option) => option.value > 0 && option.label);
  }

  getTamanoEmpresaMobileOptions(): MobileSelectOption[] {
    return this.tamanios().map((item) => ({
      value: Number(item?.pk ?? 0),
      label: String(item?.descripcion ?? '').trim()
    })).filter((option) => option.value > 0 && option.label);
  }


  /** =====================
   *  VALIDACIÓN UI
   *  ===================== */
  isInvalid(name: string): boolean {
    const c = this.form.get(name);
    return !!(c && c.invalid && (c.dirty || c.touched));
  }

  getErrorText(name: string): string {
    const c = this.form.get(name);
    if (!c || !c.errors) return '';
    if (c.errors['required']) return 'Campo obligatorio.';
    if (c.errors['email']) return 'Email no válido.';
    if (c.errors['pattern']) {
      if (name === 'telefonos') return 'Puedes usar números, letras, espacios y guiones.';
      if (name === 'nit') return 'El NIT puede incluir letras y números, por ejemplo CF, 4564854K o ND.';
    }
    return 'Valor inválido.';
  }

  private buildMissingFields(): string[] {
    const out: string[] = [];

    // required
    Object.keys(this.labels).forEach(key => {
      const c = this.form.get(key);
      if (!c) return;
      if (c.errors?.['required']) out.push(this.labels[key]);
    });

    if (this.form.get('email')?.errors?.['email']) out.push('Email no válido');
    if (this.form.get('telefonos')?.errors?.['pattern']) out.push('Teléfonos (solo letras, números y signos comunes)');
    if (this.form.get('nit')?.errors?.['pattern']) out.push('NIT (puede incluir letras y números)');
    if (this.form.errors?.['primeraJornadaInvalida']) out.push('Primera jornada (hora final mayor a inicial)');
    if (this.form.errors?.['primeraJornadaExcesiva']) out.push('Primera jornada (duración válida)');
    if (this.form.errors?.['segundaJornadaInvalida']) out.push('Segunda jornada (hora final mayor a inicial)');
    if (this.form.errors?.['segundaJornadaExcesiva']) out.push('Segunda jornada (duración válida)');
    if (this.form.errors?.['traslapeJornadas']) out.push('Jornadas (sin traslape)');
    if ((this.articleCmp?.buildArticulosPayload()?.length ?? 0) === 0) out.push('Agregar al menos un artículo válido');

    return Array.from(new Set(out));
  }

  private openValidationModal(fields: string[]): void {
    this.missingFields.set(Array.from(new Set(fields)));
    this.showValidationModal.set(this.missingFields().length > 0);
  }

  /** =====================
   *  FECHA/HORA LOCAL (DISPOSITIVO)
   *  ===================== */
  private nowLocalDateTime(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private resolveEmail(value: string | null | undefined): string {
    const email = String(value ?? '').trim();
    return email || DEFAULT_EMAIL;
  }

  private normalizePhoneText(value: unknown): string {
    return String(value ?? '').trim();
  }

  private normalizeNit(value: unknown): string {
    return String(value ?? '').trim().toUpperCase();
  }

  private applyDefaultEmail(control: AbstractControl | null): void {
    if (!control) return;
    control.setValue(this.resolveEmail(control.value as string | null | undefined), { emitEvent: false });
  }

  applyDefaultMainEmail(): void {
    this.applyDefaultEmail(this.form.get('email'));
  }

  applyDefaultContactEmail(): void {
    this.applyDefaultEmail(this.contactForm.get('email'));
  }

  preventInvalidIntegerInput(event: KeyboardEvent): void {
    if (['e', 'E', '+', '-', '.', ','].includes(event.key)) {
      event.preventDefault();
    }
  }

  normalizeNonNegativeInteger(controlName: 'ordenRuta'): void {
    const control = this.form.get(controlName);
    if (!control) return;

    const numericValue = Number(control.value);
    const safeValue = Number.isFinite(numericValue) && numericValue >= 0
      ? Math.trunc(numericValue)
      : 0;

    control.patchValue(safeValue as any, { emitEvent: false });
  }

  /** =====================
   *  GEOLOCALIZACIÓN
   *  ===================== */
  useCurrentLocation(): void {
    if (this.geoLoading()) return;

    if (!navigator.geolocation) {
      this.error.set('El navegador no soporta geolocalización.');
      return;
    }

    this.geoLoading.set(true);
    this.error.set(null);

    navigator.geolocation.getCurrentPosition(
      pos => {
        this.form.patchValue({
          gpsLatitud: pos.coords.latitude,
          gpsLongitud: pos.coords.longitude,
          gpsAltitud: pos.coords.altitude ?? 0,
          gpsFecha: this.nowLocalDateTime(),
        });
        this.geoLoading.set(false);
      },
      () => {
        this.geoLoading.set(false);
        this.error.set('No se pudo obtener la ubicación.');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }


  /** =====================
   *  SUBMIT (ANTI DUPLICADO)
   *  ===================== */
  onSubmit(): void {

    const userId = this.getLoggedUserId();

    if (!userId) {
      this.error.set('Sesión no válida. Inicie sesión nuevamente.');
      return;
    }

    if (this.loading() || this.success() || this.form.disabled) return;

    this.showValidationModal.set(false);
    this.missingFields.set([]);
    this.error.set(null);

    this.form.markAllAsTouched();

    this.applyDefaultEmail(this.form.get('email'));

    const validationIssues = this.buildMissingFields();
    if (validationIssues.length > 0) {
      this.openValidationModal(validationIssues);
      return;
    }

    if (!this.form.get('gpsFecha')?.value) {
      this.form.patchValue({ gpsFecha: this.nowLocalDateTime() }, { emitEvent: false });
    }

    this.loading.set(true);

    const raw: any = this.form.getRawValue();
    if (!raw.gpsLatitud || !raw.gpsLongitud) {
      this.error.set('Debes obtener la ubicación GPS antes de guardar.');
      this.loading.set(false);
      return;
    }

    // ✅ arma "fuente" (lo que tu SP lee en $.fuente)
    const fuente: SourceSurveyPayload['fuente'] = {
      codigoINE: raw.codigoINE,
      razonSocial: raw.razonSocial,
      nombre: raw.nombre,
      direccion: raw.direccion,

      fkCodigoPostal: Number(raw.fkCodigoPostal),
      fkDepartamento: Number(raw.fkDepartamento),
      fkMunicipio: Number(raw.fkMunicipio),
      periodo: Number(raw.periodo),
      ordenRuta: raw.ordenRuta != null && raw.ordenRuta !== '' ? Number(raw.ordenRuta) : 0,

      // si zona puede ser NOT NULL en BD, evita null
      zona: raw.zona != null && raw.zona !== '' ? Number(raw.zona) : 0,

      gpsLatitud: raw.gpsLatitud,
      gpsLongitud: raw.gpsLongitud,
      gpsAltitud: raw.gpsAltitud ?? 0,
      gpsFecha: raw.gpsFecha,

      telefonos: this.normalizePhoneText(raw.telefonos),
      email: this.resolveEmail(raw.email),

      fkUsuario: Number(raw.fkUsuario),
      fkPeriodicidadVisita: Number(raw.fkPeriodicidadVisita),
      fkDiaVisita: Number(raw.fkDiaVisita),

      horaInicioPrimeraJornada: raw.horaInicioPrimeraJornada ?? '',
      horaFinalPrimeraJornada: raw.horaFinalPrimeraJornada ?? '',
      horaInicioSegundaJornada: raw.horaInicioSegundaJornada ?? '',
      horaFinalSegundaJornada: raw.horaFinalSegundaJornada ?? '',

      requiereAudio: raw.requiereAudio ?? 'N',
      requiereImagen: raw.requiereImagen ?? 'N',

      nit: this.normalizeNit(raw.nit),
      fkActividadEmpresa: raw.fkActividadEmpresa != null && raw.fkActividadEmpresa !== '' ? Number(raw.fkActividadEmpresa) : null,
      fkTamanoEmpresa: raw.fkTamanoEmpresa != null && raw.fkTamanoEmpresa !== '' ? Number(raw.fkTamanoEmpresa) : null,

      codigoCIIU: raw.codigoCIIU ?? '',
      comentarios: raw.comentarios ?? '',
      referencia: raw.referencia ?? '',
      informacionAdicional: raw.informacionAdicional ?? '',
      estado: raw.estado ?? 'P'
    };

    // ✅ contactos (tu SP lee title/name/position/phones/email/comments/order/status)
    const contactos = (this.pendingContacts ?? []).map(c => ({
      title: c.title ?? '',
      name: c.name ?? '',
      position: c.position ?? '',
      phones: c.phones ?? '',
      email: this.resolveEmail(c.email),
      comments: c.comments ?? '',
      order: Number(c.order ?? 0),
      status: (c.status ?? 'P') as 'A' | 'I' | 'P'
    }));

    // ✅ artículos desde el hijo (buildArticulosPayload)
    const articulos = this.articleCmp?.buildArticulosPayload() ?? [];

    const payload: SourceSurveyPayload = {
      fuente,
      contactos,
      articulos,
      segUsuario: userId,
      segTransaccion: 32
    };

    this.surveyService.saveSurvey(payload)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res: SourceSurveyResponse) => {
          const ok = this.readSurveyOk(res);

          if (ok !== 1) {
            this.error.set(this.readSurveyMessage(res));
            return;
          }

          this.resetAll();
          this.success.set(true);

        },
        error: (err: HttpErrorResponse) => {
          console.error('Error al guardar levantamiento', { payload, err });
          const message = this.readSurveyHttpError(err);
          this.articleCmp?.handleSaveFailure(message);
          this.error.set(message);
        }

      });



  }

  closeSuccessModal(): void {
    this.success.set(false);
  }

  closeValidationModal(): void {
    this.showValidationModal.set(false);
  }

  private readSurveyOk(res: SourceSurveyResponse | null | undefined): number {
    return Number(res?.ok ?? res?.OK ?? 0);
  }

  private readSurveyMessage(res: SourceSurveyResponse | null | undefined): string {
    const mensaje = res?.mensaje ?? res?.MENSAJE;
    const detail = res?.detail ?? res?.DETAIL;

    if (mensaje && detail) {
      return `${mensaje}: ${detail}`;
    }

    return mensaje ?? detail ?? 'Error al guardar levantamiento.';
  }

  private readSurveyHttpError(err: HttpErrorResponse): string {
    const body = err?.error;

    if (typeof body === 'string' && body.trim()) {
      return body;
    }

    if (body && typeof body === 'object') {
      return this.readSurveyMessage(body as SourceSurveyResponse);
    }

    return err.message || 'Error al guardar levantamiento.';
  }

  isHoraInvalidaPrimera(): boolean {
    const errors = this.form.errors;
    if (!errors) return false;

    const touched =
      this.form.get('horaInicioPrimeraJornada')?.touched ||
      this.form.get('horaFinalPrimeraJornada')?.touched;

    if (!touched) return false;

    return !!(
      errors['primeraJornadaInvalida'] ||
      errors['primeraJornadaExcesiva']
    );
  }

  isHoraInvalidaSegunda(): boolean {
    const errors = this.form.errors;
    if (!errors) return false;

    const touched =
      this.form.get('horaInicioSegundaJornada')?.touched ||
      this.form.get('horaFinalSegundaJornada')?.touched;

    if (!touched) return false;

    return !!(
      errors['segundaJornadaInvalida'] ||
      errors['segundaJornadaExcesiva']
    );
  }



  /** =====================
   *  CONTACTOS (ANTI DUPLICADO)
   *  ===================== */
  private normalize(v?: string | null): string {
    return (v ?? '').trim().toLowerCase();
  }

  generateFountainNumber(silent = false): void {
    if (this.generatingNumber()) return;

    this.generatingNumber.set(true);
    if (!silent) {
      this.error.set(null);
    }

    this.sourceService.generateNumber()
      .pipe(finalize(() => this.generatingNumber.set(false)))
      .subscribe({
        next: res => {
          const number = Number(res?.number ?? 0);
          if (!number) {
            if (!silent) {
              this.error.set('No se pudo generar el número de fuente.');
            }
            return;
          }

          this.form.patchValue({ codigoINE: String(number) }, { emitEvent: false });
        },
        error: () => {
          if (!silent) {
            this.error.set('Error al generar el número de fuente.');
          }
        }
      });
  }

  private contactKey(c: SourceContact): string {
    const email = this.normalize(c.email);
    if (email && email !== DEFAULT_EMAIL) return `email:${email}`;
    return [
      this.normalize(c.name),
      this.normalize(c.phones),
      this.normalize(c.position),
      this.normalize(c.title)
    ].join('|');
  }



  removeContact(index: number): void {
    this.pendingContacts.splice(index, 1);
    this.hasUnsavedChanges.set(true);

  }


  openNewContact(): void {
    this.editingIndex = null;
    this.contactForm.reset({
      order: this.pendingContacts.length + 1
    });
    this.showContactForm.set(true);
  }

saveContact(): void {

  if (this.contactForm.invalid) {
    this.contactForm.markAllAsTouched();
    return;
  }

  const v = this.contactForm.value;

  const contact: UiContact = {
    name: v.name!,
    title: v.title ?? '',
    position: v.position ?? '',
    phones: this.normalizePhoneText(v.phones),
    email: this.resolveEmail(v.email),
    order: Number(v.order ?? 0),
    comments: v.comments ?? '',
    status: 'P'
  };



  // ✅ Ahora sí guarda
  if (this.editingIndex === null) {
    this.pendingContacts.push(contact);
  } else {
    this.pendingContacts[this.editingIndex] = contact;
  }

  this.contactForm.reset({
    order: this.pendingContacts.length + 1
  });

  this.editingIndex = null;
  this.ordenarContactos();
  this.showContactForm.set(false);
  this.hasUnsavedChanges.set(true);
}

  private ordenarContactos(): void {
    this.pendingContacts.sort((a, b) => {
      const orderA = a.order ?? 0;
      const orderB = b.order ?? 0;
      return orderA - orderB;
    });
  }


  editContact(c: UiContact, index: number): void {
    this.editingIndex = index;

    this.contactForm.patchValue({
      name: c.name,
      title: c.title,
      position: c.position,
      phones: c.phones,
      email: c.email,
      order: c.order,
      comments: c.comments
    });

    this.showContactForm.set(true);
  }

  toggleContactForm(): void {
    if (this.showContactForm()) {
      this.editingIndex = null;
      this.contactForm.reset({
        order: this.pendingContacts.length + 1
      });
    }

    this.showContactForm.set(!this.showContactForm());
  }



  contactForm = this.fb.group({
    name: ['', [
      Validators.required,
      Validators.minLength(3)
    ]],

    title: [''],

    position: [''],

    phones: ['', [
      Validators.required,
      Validators.maxLength(50),
      Validators.pattern(PHONE_TEXT_PATTERN)
    ]],

    email: ['', [
      Validators.email
    ]],

    order: [0, [
      Validators.required,
      Validators.min(0)
    ]],

    comments: ['', [
      Validators.maxLength(250)
    ]]
  });


  /** =====================
   *  DEP -> MUNICIPIO
   *  ===================== */
  onDepartamentoChange(): void {
    const depId = Number(this.form.get('fkDepartamento')?.value);
    this.codigosPostales.set([]);
    this.codigoPostalDescripcion = '';
    this.form.patchValue({
      fkMunicipio: null,
      fkCodigoPostal: null
    });

    // si no hay dep seleccionado, limpia municipios
    if (!depId) {
      this.municipalities.set([]);
      this.form.patchValue({ fkMunicipio: null });
      return;
    }

    this.municipalityService.getMunicipalityByDepartament(depId).subscribe({
      next: (data: any[]) => {
        // tu backend puede devolver pk o id, por eso lo hago “tolerante”
        const validIds = (data ?? [])
          .map(m => Number(m.pk ?? m.id ?? m.municipioId))
          .filter(n => !Number.isNaN(n));

        const municipalities = Object.entries(MunicipalityGT)
          .filter(([_, id]) => validIds.includes(Number(id)))
          .map(([name, id]) => ({ id: Number(id), nombre: name }));

        this.municipalities.set(municipalities);

        const selectedMunicipality = this.setFirstFormOptionIfNeeded(
          'fkMunicipio',
          municipalities,
          item => item.id
        );

        if (selectedMunicipality != null) {
          this.onMunicipioChange();
        } else {
          this.form.patchValue({ fkMunicipio: null, fkCodigoPostal: null }, { emitEvent: false });
          this.codigosPostales.set([]);
          this.codigoPostalDescripcion = '';
        }
      },
      error: () => {
        this.municipalities.set([]);
        this.form.patchValue({ fkMunicipio: null });
        this.error.set('No se pudieron cargar los municipios.');
      }
    });

  }


  /** =====================
   *  MUNICIPIO -> CÓDIGOS POSTALES
   *  ===================== */onMunicipioChange(): void {
    const municipioId = Number(this.form.get('fkMunicipio')?.value);
    const depId = Number(this.form.get('fkDepartamento')?.value);
    const zona = Number(this.form.get('zona')?.value);

    if (!municipioId || !depId) {
      this.codigosPostales.set([]);
      this.form.patchValue({ fkCodigoPostal: null });
      this.codigoPostalDescripcion = '';
      return;
    }

    const referencia = zona && zona > 0 ? `Zona ${zona}` : '0';
    this.codigoPostalQuery$.next({ depId, municipioId, referencia });
  }


  onZonaChange(): void {
    this.onMunicipioChange();
  }

  private getMobileSelectionOptions(controlName: MobileSelectControlName): MobileSelectOption[] {
    switch (controlName) {
      case 'fkDepartamento':
        return this.getDepartamentoMobileOptions();
      case 'fkMunicipio':
        return this.getMunicipioMobileOptions();
      case 'fkUsuario':
        return this.getUsuarioMobileOptions();
      case 'periodo':
        return this.getPeriodoMobileOptions();
      case 'fkPeriodicidadVisita':
        return this.getPeriodicidadMobileOptions();
      case 'fkDiaVisita':
        return this.getDiaVisitaMobileOptions();
      case 'requiereAudio':
      case 'requiereImagen':
        return this.yesNoOptions;
      case 'fkActividadEmpresa':
        return this.getActividadMobileOptions();
      case 'fkTamanoEmpresa':
        return this.getTamanoEmpresaMobileOptions();
      case 'estado':
        return this.statusOptions;
      default:
        return [];
    }
  }

  private normalizeSearchText(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }


  private resetAll(): void {

    // 🔹 reset flags
    this.success.set(false);
    this.showValidationModal.set(false);
    this.error.set(null);
    this.loading.set(false);
    this.missingFields.set([]);

    // 🔹 reset formulario principal
    this.form.reset({
      estado: 'P',
      requiereAudio: 'N',
      requiereImagen: 'N',
      ordenRuta: 0,
      zona: null,
      email: '',
      ...DEFAULT_JORNADAS
    });

    this.form.markAsPristine();
    this.form.markAsUntouched();

    // 🔹 limpiar dependencias
    this.codigosPostales.set([]);
    this.municipalities.set([]);

    // 🔹 limpiar contactos
    this.pendingContacts = [];
    this.savedContacts = [];
    this.editingIndex = null;
    this.contactForm.reset();

    // 🔹 limpiar artículos (componente hijo)
    this.articleCmp?.reset(); // ⬅️ lo explico abajo

    // 🔹 ids internos
    this.createdSourceId = 0;

    // 🔹 fecha GPS nueva
    this.form.patchValue(
      { gpsFecha: this.nowLocalDateTime() },
      { emitEvent: false }
    );
    this.hasUnsavedChanges.set(false);

  }


  private getLoggedUserId(): number | null {
    const raw = localStorage.getItem('authUser');
    if (!raw) return null;

    try {
      const user = JSON.parse(raw);
      return Number(user.pkUsuario) || null;
    } catch {
      return null;
    }
  }


  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges() && !this.success()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  canDeactivate(): boolean {
    if (this.hasUnsavedChanges() && !this.success()) {
      return confirm(
        'Hay información sin guardar. ¿Seguro que desea salir?'
      );
    }
    return true;
  }

  onCancel(): void {
    if (this.loading()) return;

    this.router.navigate(['/home'], { replaceUrl: true });
  }



}
